import type { LOANREQUEST_BUCKET } from '../loanRequest';
import type { ORGANIZATION } from '../various';

export const USER_ROLES = {
    accountant_manager: 'accountant_manager',
    admin: 'admin',
    adviser: 'adviser',
    corporation_director: 'corporation_director',
    division_leader: 'division_leader',
    email_manager: 'email_manager',
    guest: 'guest',
    team_leader: 'team_leader',
    user: 'user',
} as const;

export type USER_ROLE = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// a corporation director may never hand out admin or director rights
export const USER_ROLES_ASSIGNABLE_BY_DIRECTOR = [
    USER_ROLES.accountant_manager,
    USER_ROLES.adviser,
    USER_ROLES.division_leader,
    USER_ROLES.email_manager,
    USER_ROLES.team_leader,
] as const;

/**
 * Roles whose users can own buckets, take part in the Leadhahn rotation and be assigned leads
 * directly. A team leader carries leads on top of leading their team.
 */
export const USER_LEAD_CARRYING_ROLES = [USER_ROLES.adviser, USER_ROLES.team_leader] as const;

// internal-only roles that always see Schufa, independent of division or partner level
export const SCHUFA_ACCESS_EXEMPT_ROLES = [USER_ROLES.admin, USER_ROLES.accountant_manager] as const;

export type SCHUFA_ACCESS_EXEMPT_ROLE = (typeof SCHUFA_ACCESS_EXEMPT_ROLES)[number];

export type USER_LEAD_CARRYING_ROLE = (typeof USER_LEAD_CARRYING_ROLES)[number];

const { corporation_director, guest, team_leader, user, ...defaultRoles } = USER_ROLES;

export const USER_ROLES_WITHOUT_GUEST_AND_USER = { ...defaultRoles, corporation_director, team_leader } as const;

export type USER_ROLE_WITHOUT_GUEST_AND_USER =
    (typeof USER_ROLES_WITHOUT_GUEST_AND_USER)[keyof typeof USER_ROLES_WITHOUT_GUEST_AND_USER];

export const USER_DEFAULT_ROLES = { ...defaultRoles } as const;
export const USER_CORPORATION_ROLES = { corporation_director } as const;
export const USER_TEAM_ROLES = { team_leader } as const;

export type User = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    iat: number;
    exp: number;
    corporationId: string | null;
    divisionId: null | string;
    organization: ORGANIZATION;
    role: USER_ROLE_WITHOUT_GUEST_AND_USER;
    europacePlatforms: {
        europace2: {
            partnerId: null | string;
        };
        starpool: {
            partnerId: null | string;
        };
    } | null;
    signatureId: null | string;
    sub: string; // TODO - what is this?
    telephone: null | string;
    ownedBuckets: Array<LOANREQUEST_BUCKET>;
    procheckId?: string | null;
    impersonator?: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
    };
};

export interface UserState {
    user: null | User;
    rtDecoded: null | {
        breakingEvent: string;
        exp: number;
        iat: number;
        id: string;
        sub: string; // TODO - what is this
    };
}
